import { ColumnDef } from "@tanstack/react-table";
import { PersonEventRegistration } from "../types";
import { CellContext } from "@tanstack/react-table";
import {
  getOrderStatusTagExtras,
  getOrderStatusTagLabel,
  getOrderStatusTagType,
  renderEventStatus
} from "@/pages/events/_utils";
import IconButton from "@/components/IconButton";
import { actionId, type TableRowMenu } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { useNavigate } from "@/router";
import { toFormat, toDateFormat } from "@/lib/dateHelpers";
import Tooltip from "@/components/Tooltip";
import { buildTwoLineColumn, buildExpandableCountColumn, buildTagColumn } from "@/components/Table";
import { getEventTypeName } from "@/pages/events/[eventSlug]/utils";

interface GetColumnsProps {
  setMenu: (menu: TableRowMenu) => void;
  navigate: ReturnType<typeof useNavigate>;
  pgaId: string;
}

const renderEventStatusWithCancellationDate = (status: string, eventCancelledAt?: string) => {
  const baseTag = renderEventStatus(status);

  // If it's a canceled event and we have a cancellation date, wrap with tooltip
  if (status === "Canceled" && eventCancelledAt) {
    const formattedDate = toDateFormat(eventCancelledAt);
    return (
      <Tooltip
        supportingText={`Event Canceled on ${formattedDate}`}
        variant="plain"
        color="black"
        menuCorner="end-start"
        anchorCorner="start-start"
        positioning="fixed"
        anchorClassName="w-fit"
      >
        {baseTag}
      </Tooltip>
    );
  }

  return baseTag;
};

export const getCancellationColumns = ({
  setMenu,
  navigate,
  pgaId
}: GetColumnsProps): ColumnDef<PersonEventRegistration, any>[] => [
  buildTwoLineColumn<PersonEventRegistration>({
    accessorKey: "eventTitle",
    header: "Event Name",
    getPrimaryText: (row) => row.eventTitle,
    getSecondaryText: (row) => getEventTypeName(row) ?? "",
    secondaryLabel: "Type",
    size: 280
  }),
  {
    accessorKey: "startsAt",
    header: "Start Date",
    enableSorting: false,
    cell: ({ row }: { row: { original: PersonEventRegistration } }) => {
      return <div>{toFormat(row.original.startsAt)}</div>;
    }
  },
  {
    accessorKey: "endsAt",
    header: "End Date",
    enableSorting: false,
    cell: ({ row }: { row: { original: PersonEventRegistration } }) => {
      return <div>{toFormat(row.original.endsAt)}</div>;
    }
  },
  buildExpandableCountColumn<PersonEventRegistration>({
    accessorKey: "sessions",
    header: "Sessions",
    getCount: (row) => row.sessions?.length || 0
  }),
  {
    accessorKey: "facility",
    header: "Location",
    enableSorting: false
  },
  {
    accessorKey: "canceledAt",
    header: "Canceled",
    enableSorting: false,
    cell: ({ row }: { row: { original: PersonEventRegistration } }) => {
      return row.original.canceledAt ? toFormat(row.original.canceledAt) : null;
    }
  },
  {
    accessorKey: "totalPaid",
    header: "Amount Refunded",
    enableSorting: false
  },
  buildTagColumn<PersonEventRegistration>({
    accessorKey: "orderStatus",
    header: "Order Status",
    getTagLabel: (row) => (row.orderId ? getOrderStatusTagLabel(row.orderStatus) : null),
    getTagType: (row) => getOrderStatusTagType(row.orderStatus),
    getTagExtras: (row) =>
      getOrderStatusTagExtras({
        orderStatus: row.orderStatus,
        amountRefunded: row.totalPaid,
        isCanceled: !!row.canceledAt,
        paymentStatus: row.paymentStatus
      })
  }),
  {
    accessorKey: "status",
    header: "Event Stage",
    enableSorting: false,
    cell: ({ row }) => <>{renderEventStatusWithCancellationDate(row.original.status, row.original.eventCancelledAt)}</>,
    meta: {
      headerClassName: "hide-header-in-column"
    }
  },
  {
    cell: (cellProps: CellContext<PersonEventRegistration, unknown>) => {
      const actions: Action[] = [
        {
          text: "View Registration",
          onClick: () =>
            navigate(
              `/events/${cellProps.row.original.eventSlug}/registrations/${cellProps.row.original.id}?return_to=/people/${pgaId}/events/cancellations` as any
            )
        },
        {
          text: "View Event",
          onClick: () => navigate(`/events/${cellProps.row.original.eventSlug}` as any)
        },
        ...(cellProps.row.original.orderId
          ? [
              {
                text: "View Order",
                onClick: () => navigate(`/billing/orders/${cellProps.row.original.orderId}` as any)
              } satisfies Action
            ]
          : [])
      ];

      return (
        actions.length > 0 && (
          <div
            className="text-right flex flex-row justify-end"
            data-testid="menu-icon"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <IconButton
              id={actionId(cellProps.row.id)}
              onClick={() => setMenu({ rowId: cellProps.row.id, actions })}
              data-testid="actions"
              title="actions"
              name="more_vert"
              iconClassName="font-extrabold"
              skipMargin
            />
          </div>
        )
      );
    },
    id: "actions",
    size: 100,
    meta: {
      skipEmptyState: true
    }
  }
];
